// Story 7.7 — admin cockpit "Post to feed" surface at /admin/feed/editorial.
//
// Left column: compose form (title, autosize body, optional hero-image
// https URL with preview, optional external URL, comma-separated tags,
// Publish). Right column: live WYSIWYG preview rendered through the
// REAL <EditorialCard /> from Story 7.6 — what the admin sees is what
// the feed renders. Below: recent editorial history with per-row Hide
// (soft-delete via content_service::soft_delete on the backend).
//
// Renders inside <AdminLayout> (App.tsx nests it under the guarded
// /admin layout route, superseding the story's standalone-route AC).

import { useEffect, useState, type FormEvent } from "react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

import { Button } from "@greenhold/components";

import { EditorialCard } from "../features/feed/EditorialCard";
import type { FeedCard } from "../features/feed/useFeed";
import {
  useCreateEditorialPost,
  type CreateEditorialPostError,
} from "../features/admin-feed/useCreateEditorialPost";
import { useEditorialDetail } from "../features/admin-feed/useEditorialDetail";
import { useHideEditorial } from "../features/admin-feed/useHideEditorial";
import { useRecentEditorial } from "../features/admin-feed/useRecentEditorial";
import { useUpdateEditorialPost } from "../features/admin-feed/useUpdateEditorialPost";

const TITLE_MAX = 300;
const BODY_MAX = 4000;

function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

export function AdminFeedEditorialPage() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [externalUrl, setExternalUrl] = useState("");
  const [tagsRaw, setTagsRaw] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);

  const createMut = useCreateEditorialPost();
  const updateMut = useUpdateEditorialPost();
  const hideMut = useHideEditorial();
  const recent = useRecentEditorial();

  // Edit mode: ?edit={id} (deep-linked from the feed's admin Edit
  // button and the recent-history rows below). The detail fetch seeds
  // the form ONCE per id — refetches don't clobber in-progress edits.
  const [searchParams, setSearchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const detail = useEditorialDetail(editId);
  const [seededId, setSeededId] = useState<string | null>(null);

  useEffect(() => {
    if (editId && detail.data && detail.data.id === editId && seededId !== editId) {
      setTitle(detail.data.title);
      setBody(detail.data.body);
      setMediaUrl(detail.data.media_url ?? "");
      setExternalUrl(detail.data.external_url ?? "");
      setTagsRaw(detail.data.tags.join(", "));
      setInlineError(null);
      setSeededId(editId);
    }
    if (!editId && seededId !== null) {
      // Left edit mode — back to a blank compose form.
      setTitle("");
      setBody("");
      setMediaUrl("");
      setExternalUrl("");
      setTagsRaw("");
      setInlineError(null);
      setSeededId(null);
    }
  }, [editId, detail.data, seededId]);

  function exitEditMode() {
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next);
  }

  const tags = tagsRaw
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

  const previewCard: FeedCard = {
    id: "preview",
    source_kind: "admin_editorial",
    creator_user_id: null,
    creator_handle: null,
    creator_display_name: null,
    title: title || "Your headline goes here",
    body: body || null,
    media_url: mediaUrl && isHttpsUrl(mediaUrl) ? mediaUrl : null,
    external_url: externalUrl || null,
    published_at: new Date().toISOString(),
    is_pinned: false,
    is_promo: false,
    source_specific: {},
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setInlineError(null);
    if (!title.trim()) {
      setInlineError("Title is required.");
      return;
    }
    if (mediaUrl && !isHttpsUrl(mediaUrl)) {
      setInlineError("Hero image URL must be a valid https URL.");
      return;
    }
    if (externalUrl && !isHttpsUrl(externalUrl)) {
      setInlineError("External URL must be a valid https URL.");
      return;
    }
    const fields = {
      title: title.trim(),
      body,
      media_url: mediaUrl || null,
      external_url: externalUrl || null,
      tags,
    };
    if (editId) {
      updateMut.mutate(
        { id: editId, ...fields },
        {
          onSuccess: () => {
            toast.success("Editorial updated.");
            exitEditMode();
          },
          onError: (error: CreateEditorialPostError) => {
            setInlineError(error.message);
          },
        },
      );
      return;
    }
    createMut.mutate(fields, {
      onSuccess: () => {
        toast.success("Posted to feed.");
        setTitle("");
        setBody("");
        setMediaUrl("");
        setExternalUrl("");
        setTagsRaw("");
      },
      onError: (error: CreateEditorialPostError) => {
        setInlineError(error.message);
      },
    });
  };

  return (
    <main className="mx-auto w-full max-w-6xl px-6 py-8">
      <h1 className="m-0 font-heading text-3xl text-brand-paper-100">
        {editId ? "Edit editorial" : "Post to feed"}
      </h1>
      <p className="m-0 mt-1 font-body text-sm text-brand-paper-100/60">
        {editId
          ? "Changes publish in place — the card keeps its feed position."
          : "Editorial cards publish immediately with source “Greenhold Newsroom”. No review queue — check the preview before you hit Publish."}
      </p>
      {editId && detail.isPending ? (
        <p className="m-0 mt-2 font-body text-sm text-brand-paper-100/60">
          Loading post…
        </p>
      ) : null}
      {editId && detail.isError ? (
        <p className="m-0 mt-2 font-body text-sm text-red-400" role="alert">
          Couldn&apos;t load that post for editing: {detail.error.message}
        </p>
      ) : null}

      <div className="mt-6 grid gap-8 lg:grid-cols-2">
        {/* ---- Compose form ---- */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <label className="block text-sm">
            <span className="mb-1 block font-body text-brand-paper-100/70">
              Title{" "}
              <span className="font-mono">
                ({title.length} / {TITLE_MAX})
              </span>
            </span>
            <input
              type="text"
              value={title}
              maxLength={TITLE_MAX}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded border border-brand-gold-500/20 bg-brand-forest-800 px-3 py-2 font-body text-brand-paper-100"
              data-testid="editorial-title-input"
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-body text-brand-paper-100/70">
              Body{" "}
              <span className="font-mono">
                ({body.length} / {BODY_MAX})
              </span>
            </span>
            <textarea
              value={body}
              maxLength={BODY_MAX}
              onChange={(e) => setBody(e.target.value)}
              className="min-h-40 max-h-96 w-full resize-y rounded border border-brand-gold-500/20 bg-brand-forest-800 px-3 py-2 font-body text-brand-paper-100"
              data-testid="editorial-body-input"
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-body text-brand-paper-100/70">
              Hero image URL (optional, https)
            </span>
            <input
              type="url"
              value={mediaUrl}
              onChange={(e) => setMediaUrl(e.target.value)}
              placeholder="https://…"
              className="w-full rounded border border-brand-gold-500/20 bg-brand-forest-800 px-3 py-2 font-mono text-sm text-brand-paper-100"
              data-testid="editorial-media-input"
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-body text-brand-paper-100/70">
              External URL (optional, https — &ldquo;Read more&rdquo; link-out)
            </span>
            <input
              type="url"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              placeholder="https://…"
              className="w-full rounded border border-brand-gold-500/20 bg-brand-forest-800 px-3 py-2 font-mono text-sm text-brand-paper-100"
              data-testid="editorial-external-input"
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-body text-brand-paper-100/70">
              Tags (comma-separated, stored for future filtering)
            </span>
            <input
              type="text"
              value={tagsRaw}
              onChange={(e) => setTagsRaw(e.target.value)}
              placeholder="launch, cs, editorial"
              className="w-full rounded border border-brand-gold-500/20 bg-brand-forest-800 px-3 py-2 font-body text-sm text-brand-paper-100"
              data-testid="editorial-tags-input"
            />
            {tags.length > 0 ? (
              <span className="mt-2 flex flex-wrap gap-1.5">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-brand-gold-500/30 px-2 py-0.5 font-body text-xs text-brand-gold-300"
                  >
                    {tag}
                  </span>
                ))}
              </span>
            ) : null}
          </label>

          {inlineError ? (
            <p
              className="m-0 font-body text-sm text-red-400"
              data-testid="editorial-inline-error"
              role="alert"
            >
              {inlineError}
            </p>
          ) : null}

          <div className="flex items-center gap-3">
            <Button
              type="submit"
              disabled={createMut.isPending || updateMut.isPending}
              data-testid="editorial-publish-button"
            >
              {editId
                ? updateMut.isPending
                  ? "Saving…"
                  : "Save changes"
                : createMut.isPending
                  ? "Publishing…"
                  : "Publish"}
            </Button>
            {editId ? (
              <Button
                type="button"
                variant="secondary"
                onClick={exitEditMode}
                disabled={updateMut.isPending}
              >
                Cancel edit
              </Button>
            ) : null}
          </div>
        </form>

        {/* ---- Live preview ---- */}
        <div>
          <p className="m-0 mb-2 font-body text-xs uppercase tracking-wide text-brand-paper-100/50">
            Feed preview
          </p>
          <div data-testid="editorial-preview">
            <EditorialCard card={previewCard} />
          </div>
        </div>
      </div>

      {/* ---- Recent editorial ---- */}
      <section className="mt-12">
        <h2 className="m-0 font-heading text-xl text-brand-paper-100">
          Recent editorial
        </h2>
        {recent.isPending ? (
          <p className="mt-3 font-body text-sm text-brand-paper-100/60">
            Loading…
          </p>
        ) : recent.isError ? (
          <p className="mt-3 font-body text-sm text-red-400">
            Couldn&apos;t load recent posts.
          </p>
        ) : recent.data && recent.data.items.length > 0 ? (
          <ul className="mt-3 flex list-none flex-col gap-2 p-0">
            {recent.data.items.map((item) => (
              <li
                key={item.id}
                className="flex items-center justify-between gap-4 rounded border border-brand-gold-500/15 bg-brand-forest-800 px-4 py-3"
                data-testid="editorial-recent-row"
              >
                <div className="min-w-0">
                  <p className="m-0 truncate font-body text-sm text-brand-paper-100">
                    {item.title}
                  </p>
                  <p className="m-0 mt-0.5 font-body text-xs text-brand-paper-100/50">
                    {new Date(item.published_at).toLocaleString()} ·{" "}
                    {item.status}
                    {item.status === "removed" ? " (hidden)" : ""}
                  </p>
                </div>
                {item.status !== "removed" ? (
                  <span className="flex shrink-0 items-center gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        const next = new URLSearchParams(searchParams);
                        next.set("edit", item.id);
                        setSearchParams(next);
                        window.scrollTo({ top: 0 });
                      }}
                      data-testid="editorial-edit-button"
                    >
                      Edit
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={hideMut.isPending}
                      onClick={() =>
                        hideMut.mutate(
                          { id: item.id },
                          {
                            onSuccess: () => toast.success("Post hidden."),
                            onError: (e) => toast.error(e.message),
                          },
                        )
                      }
                      data-testid="editorial-hide-button"
                    >
                      Hide
                    </Button>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 font-body text-sm text-brand-paper-100/60">
            No editorial posts yet.
          </p>
        )}
      </section>
    </main>
  );
}
